import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { NotePencil, Clock, FloppyDisk } from "@phosphor-icons/react";

export default function ReportMetaSection({ chantier, onChanged }) {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(chantier?.hours_worked ?? "");
  const [obs, setObs] = useState(chantier?.observations ?? "");
  const [summary, setSummary] = useState(chantier?.client_summary ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHours(chantier?.hours_worked ?? "");
    setObs(chantier?.observations ?? "");
    setSummary(chantier?.client_summary ?? "");
  }, [chantier?.id, chantier?.hours_worked, chantier?.observations, chantier?.client_summary]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/chantiers/${chantier.id}`, {
        hours_worked: hours === "" ? null : parseFloat(hours),
        observations: obs || null,
        client_summary: summary || null,
      });
      toast.success("Rapport mis à jour");
      onChanged?.();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const hasData = chantier?.hours_worked != null || chantier?.observations || chantier?.client_summary;

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between" data-testid="report-meta-toggle">
        <div className="flex items-center gap-2">
          <NotePencil size={16} className="text-white/60" weight="duotone" />
          <div className="text-[11px] uppercase tracking-widest text-white/50">Rapport — heures & observations</div>
        </div>
        {hasData ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">Rempli</span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/40">Vide</span>
        )}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest flex items-center gap-1"><Clock size={11} /> Heures passées</Label>
            <Input
              type="number"
              step="0.25"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="Ex: 4.5"
              className="bg-white/5 border-white/10 mt-1"
              data-testid="report-hours"
            />
          </div>
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Observations techniques (rapport terrain)</Label>
            <Textarea
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              rows={4}
              placeholder="Anomalies, préconisations, points de vigilance…"
              className="bg-white/5 border-white/10 mt-1"
              data-testid="report-observations"
            />
          </div>
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Résumé pour le client (rapport client)</Label>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              placeholder="Description simplifiée des travaux visible par le client…"
              className="bg-white/5 border-white/10 mt-1"
              data-testid="report-summary"
            />
          </div>
          <Button onClick={save} disabled={saving} className="w-full h-11 bg-[#005CFF] hover:bg-[#0047CC] rounded-xl" data-testid="report-meta-save">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      )}
    </div>
  );
}
